import { Fragment, useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import type {
  AllocationTreeNode,
  SubstitutionEntry,
} from "../lib/explainTypes";
import { useDisplayMode } from "../hooks/useDisplayMode";
import { formatCurrency, formatPercent } from "../../../lib/numbers";

interface Props {
  tree: AllocationTreeNode[];
  substitutions: SubstitutionEntry[];
}

const num = (s: string | null | undefined): number =>
  s == null || s === "" ? 0 : Number(s);

// Risk levels emitted by `rebalance.waterfall.build_hierarchy`. Tree
// root nests Risk → AssetClass → Symbol. When the account has the
// common two-risk shape (Growth + Cash/Defensive) the outer Risk
// level adds nesting without signal, so we flatten to one row per
// asset class. We only keep the Risk wrapper when there are three
// or more risk groups in the account (rare).
//
// Inside Defensive/Cash, the python emitter names the asset-class
// node "Unknown" when no firm-side mapping is configured. Rename
// that to "Cash" when its leaves are recognisably cash symbols, so
// the table reads as the advisor expects.
function isCashSymbol(name: string): boolean {
  return name.startsWith("CASH") || name === "Cash";
}

function flattenAssetClasses(tree: AllocationTreeNode[]): AllocationTreeNode[] {
  const acs: AllocationTreeNode[] = [];
  for (const risk of tree) {
    for (const ac of risk.children ?? []) {
      const leaves = ac.children ?? [];
      const renamed =
        ac.name === "Unknown" && leaves.every((l) => isCashSymbol(l.name))
          ? { ...ac, name: "Cash" }
          : ac;
      acs.push(renamed);
    }
  }
  return acs;
}

// "Inline substitution provenance" per the proposal: a symbol that
// only exists in this account because it absorbed a substitution
// (e.g. MSFT carrying DFIC's weight under treatment=like_primary)
// gets a "← <primary>" annotation so the advisor doesn't have to
// cross-reference the Substitutions section to understand why it's
// in the target.
function substitutionLabel(
  symbol: string,
  subsByPrimary: Map<string, SubstitutionEntry>,
  subsBySecondary: Map<string, SubstitutionEntry>,
): string | null {
  const asSecondary = subsBySecondary.get(symbol);
  if (asSecondary) return `← ${asSecondary.primary} sub`;
  const asPrimary = subsByPrimary.get(symbol);
  if (asPrimary) return `→ ${asPrimary.secondary}`;
  return null;
}

// Hierarchical view of the post-constraint waterfall: per asset
// class, the target the algorithm was handed, how much of that
// target was already constrained by an upstream restriction, what
// the waterfall produced, and the rounding/overshoot gap. The
// footer totals "Constrained: $X of $Y (Z%)" answers "how much
// room did the waterfall actually have?" — when this number is
// high, drift on the upstream Asset Allocation chart is expected
// rather than a failure.
//
// Data is the python `serialize_tree` output emitted on
// `ConstrainedAllocationData.allocation_tree`. No engine change is
// needed to surface it.
export default function WaterfallSection({ tree, substitutions }: Props) {
  const { mode } = useDisplayMode();

  const assetClasses = useMemo(() => flattenAssetClasses(tree), [tree]);

  const { rootTarget, rootAllocated } = useMemo(() => {
    let t = 0;
    let a = 0;
    for (const ac of assetClasses) {
      t += num(ac.target);
      a += num(ac.allocated);
    }
    return { rootTarget: t, rootAllocated: a };
  }, [assetClasses]);

  // The footer's "% constrained" answers "how much of the
  // investable target is locked by upstream restrictions?" Cash is
  // not investable in that sense — every account holds some, and
  // its inclusion in the denominator would drag the ratio toward
  // however much cash this account happens to need. Strip the
  // Cash asset class from both numerator and denominator so the
  // ratio reflects the equity-side constraint envelope only.
  const { investableTarget, investableConstrained } = useMemo(() => {
    let t = 0;
    let c = 0;
    for (const ac of assetClasses) {
      if (ac.name === "Cash") continue;
      t += num(ac.target);
      c += num(ac.constrained);
    }
    return { investableTarget: t, investableConstrained: c };
  }, [assetClasses]);

  const { subsByPrimary, subsBySecondary } = useMemo(() => {
    const byPrimary = new Map<string, SubstitutionEntry>();
    const bySecondary = new Map<string, SubstitutionEntry>();
    for (const s of substitutions) {
      byPrimary.set(s.primary, s);
      bySecondary.set(s.secondary, s);
    }
    return { subsByPrimary: byPrimary, subsBySecondary: bySecondary };
  }, [substitutions]);

  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  if (assetClasses.length === 0) return null;

  const toggle = (name: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  // Render dollars in $ mode, weights against the tree root in
  // % mode. Using the tree's own root sum as the denominator keeps
  // percentages adding to 100% even though the python emitter
  // scales non-cash equity targets by (balance − cash) rather than
  // by balance; the SummaryRow uses balance and so its percentages
  // differ slightly from this view's. Pinning the denominator to
  // the tree root makes this table internally consistent and
  // matches what the python waterfall actually allocated against.
  const fmtAmount = (raw: string | number): string => {
    const n = typeof raw === "number" ? raw : num(raw);
    if (mode === "dollar") return formatCurrency(n);
    if (rootTarget <= 0) return "—";
    return formatPercent(n / rootTarget, 2);
  };

  const fmtDelta = (delta: number): string => {
    if (mode === "dollar") return formatCurrency(delta);
    if (rootTarget <= 0) return "—";
    return formatPercent(delta / rootTarget, 2);
  };

  const deltaClass = (delta: number): string => {
    if (Math.abs(delta) < 1) return "text-muted";
    return delta > 0 ? "text-success" : "text-danger";
  };

  const dash = "—";

  const constrainedPct =
    investableTarget > 0 ? (investableConstrained / investableTarget) * 100 : 0;

  return (
    <div className="mb-3">
      <div className="fw-semibold mb-2">Waterfall</div>
      <Table size="sm" className="mb-0">
        <thead className="text-muted">
          <tr>
            <th>Asset class / Symbol</th>
            <th className="text-end">Target</th>
            <th className="text-end">Constrained</th>
            <th className="text-end">Allocated</th>
            <th className="text-end">Δ</th>
          </tr>
        </thead>
        <tbody>
          {assetClasses.map((ac) => {
            const acT = num(ac.target);
            const acC = num(ac.constrained);
            const acA = num(ac.allocated);
            const acDelta = acA - acT;
            const isCollapsed = collapsed.has(ac.name);
            const leaves = ac.children ?? [];

            return (
              <Fragment key={ac.name}>
                <tr className="fw-semibold">
                  <td>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none text-reset"
                      aria-expanded={!isCollapsed}
                      onClick={() => toggle(ac.name)}
                    >
                      <span aria-hidden className="me-1">
                        {isCollapsed ? "▸" : "▾"}
                      </span>
                      {ac.name}
                    </button>
                  </td>
                  <td className="text-end">{fmtAmount(acT)}</td>
                  <td className="text-end">
                    {acC > 0 ? fmtAmount(acC) : dash}
                  </td>
                  <td className="text-end">{fmtAmount(acA)}</td>
                  <td className={`text-end ${deltaClass(acDelta)}`}>
                    {Math.abs(acDelta) < 0.005 ? dash : fmtDelta(acDelta)}
                  </td>
                </tr>
                {!isCollapsed &&
                  leaves.map((leaf) => {
                    const lT = num(leaf.target);
                    const lC = num(leaf.constrained);
                    const lA = num(leaf.allocated);
                    const lDelta = lA - lT;
                    const subLabel = substitutionLabel(
                      leaf.name,
                      subsByPrimary,
                      subsBySecondary,
                    );
                    return (
                      <tr key={`${ac.name}/${leaf.name}`}>
                        <td className="ps-4">
                          {leaf.name}
                          {subLabel && (
                            <span className="ms-2 small text-muted">
                              {subLabel}
                            </span>
                          )}
                        </td>
                        <td className="text-end">{fmtAmount(lT)}</td>
                        <td className="text-end">
                          {lC > 0 ? fmtAmount(lC) : dash}
                        </td>
                        <td className="text-end">{fmtAmount(lA)}</td>
                        <td className={`text-end ${deltaClass(lDelta)}`}>
                          {Math.abs(lDelta) < 0.005 ? dash : fmtDelta(lDelta)}
                        </td>
                      </tr>
                    );
                  })}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot className="text-muted small">
          <tr>
            <td colSpan={5} className="text-end pt-2">
              Constrained:{" "}
              {mode === "dollar" ? (
                <>
                  {formatCurrency(investableConstrained)} of{" "}
                  {formatCurrency(investableTarget)} (
                  {constrainedPct.toFixed(0)}%, excl. cash)
                </>
              ) : (
                <>{constrainedPct.toFixed(0)}% of target (excl. cash)</>
              )}
              {Math.abs(rootAllocated - rootTarget) >= 1 && (
                <span className="ms-3">
                  Allocated total: {fmtAmount(rootAllocated)}
                </span>
              )}
            </td>
          </tr>
        </tfoot>
      </Table>
    </div>
  );
}
